import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CooperationGroupDTO } from '@app/shared/services/dtos/cooperation/cooperation-group.dto';
import { CooperationProjectDTO } from '@app/shared/services/dtos/cooperation/cooperation-project.dto';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { CooperationUserDTO } from '@app/shared/services/dtos/cooperation/cooperation-user.dto';
import { CooperationDTO } from '@app/shared/services/dtos/cooperation/cooperation.dto';
import { CreateCooperationRequest } from '@app/shared/services/dtos/cooperation/create-cooperation-request.dto';
import { CreateCooperationUserRequest } from '@app/shared/services/dtos/cooperation/create-cooperation-user-request.dto';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CooperationsHttpService {
  private readonly httpClient = inject(HttpClient);
  private readonly base = `${environment.APIV3EndPoint}cooperations`;

  public getById(cooperationId: string): Observable<CooperationDTO> {
    return this.httpClient.get<CooperationDTO>(`${this.base}/${cooperationId}`);
  }

  public getIncoming(companyId: string, statuses: CooperationStatusEnum[] = []): Observable<CooperationDTO[]> {
    return this.httpClient.post<CooperationDTO[]>(`${this.base}/${companyId}/incoming`, statuses);
  }

  public getOutgoing(companyId: string, statuses: CooperationStatusEnum[] = []): Observable<CooperationDTO[]> {
    return this.httpClient.post<CooperationDTO[]>(`${this.base}/${companyId}/outgoing`, statuses);
  }

  public create(companyId: string, body: CreateCooperationRequest): Observable<CooperationDTO> {
    return this.httpClient.post<CooperationDTO>(`${this.base}/${companyId}`, body);
  }

  public accept(cooperationId: string): Observable<CooperationDTO> {
    return this.httpClient.get<CooperationDTO>(`${this.base}/${cooperationId}/accept`);
  }

  public reject(cooperationId: string): Observable<CooperationDTO> {
    return this.httpClient.get<CooperationDTO>(`${this.base}/${cooperationId}/reject`);
  }

  public deactivate(cooperationId: string): Observable<CooperationDTO> {
    return this.httpClient.post<CooperationDTO>(`${this.base}/${cooperationId}/deactivate`, {});
  }

  public getCooperationProjects(cooperationId: string): Observable<CooperationProjectDTO[]> {
    return this.httpClient.get<CooperationProjectDTO[]>(`${this.base}/${cooperationId}/projects`);
  }

  /** POST project IDs to set the list of projects linked to the cooperation. Replaces existing. */
  public postCooperationProjects(cooperationId: string, projectIds: string[]): Observable<CooperationProjectDTO[]> {
    return this.httpClient.post<CooperationProjectDTO[]>(`${this.base}/${cooperationId}/projects`, projectIds);
  }

  public getCooperationGroups(cooperationId: string): Observable<CooperationGroupDTO[]> {
    return this.httpClient.get<CooperationGroupDTO[]>(`${this.base}/${cooperationId}/groups`);
  }

  /** POST group IDs to set the list of groups linked to the cooperation. Replaces existing. */
  public postCooperationGroups(cooperationId: string, groupIds: string[]): Observable<CooperationGroupDTO[]> {
    return this.httpClient.post<CooperationGroupDTO[]>(`${this.base}/${cooperationId}/groups`, groupIds);
  }

  public getCooperationUsers(cooperationId: string): Observable<CooperationUserDTO[]> {
    return this.httpClient.get<CooperationUserDTO[]>(`${this.base}/${cooperationId}/users`);
  }

  public postCooperationUser(
    cooperationId: string,
    body: CreateCooperationUserRequest
  ): Observable<CooperationUserDTO> {
    return this.httpClient.post<CooperationUserDTO>(`${this.base}/${cooperationId}/users`, body);
  }

  public deleteCooperationUser(cooperationId: string, cooperationUserId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.base}/${cooperationId}/users/${cooperationUserId}`);
  }
}
